import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchPlantsForPot, deletePlantFromPot, deleteCustomPlantFromPot, updatePot } from '../../../api/api_pots';

const ConfigurePotFast = () => {
    const token = localStorage.getItem('userToken');
    const { translations } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const { hubSN, pot, irrigation_system_enabled } = location.state;
    const [name, setName] = useState(pot.name);
    const [irrigationPlan, setIrrigationPlan] = useState(irrigation_system_enabled ? (pot.irrigation_plan_times >= 2 ? 'custom' : pot.irrigation_plan_times) : 0);
    const [customIrrigationDays, setCustomIrrigationDays] = useState(irrigation_system_enabled && pot.irrigation_plan_times >= 2 ? pot.irrigation_plan_times : 0);
    const [customIrrigationHour, setCustomIrrigationHour] = useState(irrigation_system_enabled && pot.irrigation_plan_times >= 1 ? pot.irrigation_plan_hour : 1);
    const [irrigationOutIndex, setIrrigationOutIndex] = useState(irrigation_system_enabled ? pot.irrigation_out_index : 0);
    const [combinedPlants, setCombinedPlants] = useState([]);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [infoContent, setInfoContent] = useState('');

    useEffect(() => {
        if (!pot) return;
        fetchPlantsForPot(pot.id, token)
            .then(data => {
                const plantsWithCustomFlag = data.plants.map(plant => ({ ...plant, isCustom: false }));
                const customPlantsWithCustomFlag = data.customPlants.map(plant => ({ ...plant, isCustom: true }));
                setCombinedPlants([...plantsWithCustomFlag, ...customPlantsWithCustomFlag]);
            })
            .catch(error => {
                console.error('Failed to fetch plants:', error);
            });
    }, [pot, token]);

    const handleIrrigationPlanChange = (event) => {
        const selectedValue = event.target.value;
        setIrrigationPlan(selectedValue === 'custom' ? 'custom' : parseInt(selectedValue, 10));
    };

    const handleCustomDaysChange = (e) => {
        let value = parseInt(e.target.value, 10);
        if (isNaN(value)) value = 0;
        if (value < 0) value = 0;
        if (value > 7) value = 7;
        setCustomIrrigationDays(value);
    };

    const handleCustomHourChange = (e) => {
        let value = parseInt(e.target.value, 10);
        if (isNaN(value)) value = 0;
        if (value < 0) value = 0;
        if (value > 23) value = 23;
        setCustomIrrigationHour(value);
    };

    const handleSave = async (potSN) => {
        const token = localStorage.getItem('userToken');
        const updatedDevice = {
            pot_sensor_device_MAC: pot.pot_sensor_device_MAC,
            plant_ids: pot.plant_ids,
            custom_plant_ids: pot.custom_plant_ids,
            name: name,
            irrigation_plan_times: irrigation_system_enabled && irrigationPlan === 'custom' ? parseInt(customIrrigationDays, 10) : irrigationPlan,
            irrigation_plan_hour: customIrrigationHour,
            irrigation_out_index: irrigation_system_enabled ? irrigationOutIndex : 0,
            max_temperature: pot.max_Temperature,
            max_sunlight_hours: pot.max_SunlightHours,
            max_ground_humidity: pot.max_GroundHumidity,
            max_air_humidity: pot.max_AirHumidity,
            max_minerals: pot.max_Minerals,
            min_temperature: pot.min_Temperature,
            min_ground_humidity: pot.min_GroundHumidity,
            min_air_humidity: pot.min_AirHumidity,
            min_minerals: pot.min_Minerals,
            measure_times: pot.measure_times,
            send_times: pot.send_times,
        };
        try {
            await updatePot(pot.id, updatedDevice, true, token, potSN);
            alert(translations.pot_updated_successfully);
            navigate('TODO');
        } catch (error) {
            console.error(translations.failed_to_update_pot + ':', error);
            alert(translations.failed_to_update_pot);
        }
    };

    const handleDeletePlantFromPot = (plantID, isCustom) => async () => {
        const token = localStorage.getItem('userToken');
        const confirmMessage = `${translations.confirm_delete_plant_pot} `;
        if (window.confirm(confirmMessage)) {
            const deleteFunction = isCustom ? deleteCustomPlantFromPot : deletePlantFromPot;
            try {
                await deleteFunction(pot.id, plantID, token);
                setCombinedPlants(prevPlants => prevPlants.filter(plant => plant.id !== plantID));
                window.location.reload();
            } catch (error) {
                console.error(`Error deleting ${isCustom ? "custom" : ""} plant from pot:`, error);
                alert(translations.error_deleting_plant);
            }
        }
    };

    const handleBack = () => navigate(-1);

    const handleAddPlantToPot = () => {
        navigate(`/plants/addToPot`, { state: { pot } });
    };

    const handleAdvancedSettings = (hubSN, potSN) => {
        navigate(`advancedSettings`, { state: { hubSN, potSN, pot } });
    };

    const handleCalibrateIrrigationTime = (hubSN, potSN) => {
        navigate(`calibrateIrrigationTime`, { state: { hubSN, potSN } });
    };

    const handleInfo = () => {
        setInfoContent(translations.irrigation_info);
        setShowInfoPopup(true);
    };

    const closeInfoPopup = () => {
        setShowInfoPopup(false);
    };

    return (
        <>
        {showInfoPopup && (
            <div className="info-popup">
                <div className="info-popup-content">
                    <p dangerouslySetInnerHTML={{ __html: infoContent }}></p>
                    <button className="general-button" onClick={closeInfoPopup}>{translations.close}</button>
                </div>
            </div>
        )}
        <div className="sm-page">
            <div className="sm-container">
                <div className="header">
                    <button onClick={handleBack} className="back-button">&#8592;</button>
                    <h1>{translations.edit}</h1>
                    <button className='header-button' onClick={handleAddPlantToPot}>{translations.add_plants}</button>
                </div>
                <div className="form-container">
                    <div className="input-group full-width">
                        <label htmlFor="name">{translations.name}:</label>
                        <input id="name" type="text" className="general-input" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    {irrigation_system_enabled && (
                        <>
                            <div className="input-group full-width">
                                <label htmlFor="irrigation_plan_times">{translations.irrigation_scheme}:</label>
                                <div>
                                    <select id="irrigation_plan_times" value={irrigationPlan} onChange={handleIrrigationPlanChange} className="irrigate-select">
                                        <option value="0">{translations.none}</option>
                                        <option value="1">{translations.every_day}</option>
                                        <option value="-1">{translations.keep_moist}</option>
                                        <option value="-2">{translations.dynamic}</option>
                                        <option value="custom">{translations.custom_days}</option>
                                    </select>
                                    {irrigationPlan === 'custom' && (
                                        <>
                                            <input
                                                type="number"
                                                className="custom-days-input"
                                                value={customIrrigationDays || 0}
                                                onChange={handleCustomDaysChange}
                                                placeholder={translations.enter_custom_days}
                                                min={0}
                                                max={7}
                                            />
                                            <input
                                                type="number"
                                                className="custom-days-input"
                                                value={customIrrigationHour || -1}
                                                onChange={handleCustomHourChange}
                                                placeholder={translations.enter_custom_hour}
                                                min={0}
                                                max={23}
                                            />
                                        </>
                                    )}
                                    {irrigationPlan === 1 && (
                                        <input
                                            type="number"
                                            className="custom-days-input"
                                            value={customIrrigationHour || -1}
                                            onChange={handleCustomHourChange}
                                            placeholder={translations.enter_custom_hour}
                                        />
                                    )}
                                    <button type="button" onClick={handleInfo} className="info-button">&#9432;</button>
                                </div>
                            </div>
                            <div className="input-group full-width">
                                <label htmlFor="irrigation_out_index">{translations.irrigation_out_index}:</label>
                                <input id="irrigation_out_index" type="number" className="general-input" value={irrigationOutIndex} onChange={(e) => setIrrigationOutIndex(e.target.value)} />
                            </div>
                        </>
                    )}
                </div>
                <div className="sm-space-info">
                    <label>{translations.plants}:</label>
                    <ul>
                        {combinedPlants.map(plant => (
                            <li key={plant.data.id} className="space-header sm-space-header">
                                <span style={{ flex: 1 }}>{plant.data.name} {plant.isCustom ? "(CUSTOM)" : ""}</span>
                                <button
                                    type="button"
                                    onClick={handleDeletePlantFromPot(plant.data.id, plant.isCustom)}
                                    className="general-button delete-button sm-button"
                                >
                                    <i className="fi fi-rr-trash"></i>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="content">
                    {(irrigationPlan === 'custom' || irrigationPlan === 1) && (
                        <button
                            type="button"
                            onClick={() => handleCalibrateIrrigationTime(hubSN, pot.SN)}
                            className="general-button fastSettings-button"
                        >
                            {translations.calibrate_irrigation_time}
                        </button>
                    )}
                </div>
                <div className="content">
                    <button
                        type="button"
                        onClick={() => handleAdvancedSettings(hubSN, pot.SN)}
                        className="general-button edit-button"
                    >
                        {translations.advanced_settings}
                    </button>
                </div>
                <div className="content">
                    <button
                        type="button"
                        onClick={() => handleSave(pot.SN)}
                        className="general-button"
                    >
                        {translations.save}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default ConfigurePotFast;
